<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'

import outputFilesService from '@/services/embeds/plugin/output.files.service'

import { useIdeStore } from '@/stores/embeds/ide.store'
import { useEmbedConfigureStore } from '@/stores/embeds/embedConfigure.store'

const ideStore = useIdeStore()
const embedConfigStore = useEmbedConfigureStore()

const { outputFiles } = storeToRefs(ideStore)
const { getOnEditMode } = storeToRefs(embedConfigStore)
const showOutputFiles = computed(() => {
  return outputFiles && outputFiles.value.length > 0
})

/**
 * Gets the download url for the output file
 * @param file The file name
 */
const downloadFile = (file: string) => {
  const link = document.createElement('a')
  link.href = `/api/projectSync/downloadItem?projectKey=1001&name=${encodeURIComponent(
    file
  )}&type=output`
  link.download = file
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

onMounted(async () => {
  await new Promise((resolve) => setTimeout(resolve, 100))
  if (getOnEditMode.value === false) {
    outputFilesService.initOutputFiles()
  }
})
onBeforeUnmount(async () => {
  if (getOnEditMode.value === false) {
    await outputFilesService.destroyOutputFiles()
  }
  await new Promise((resolve) => setTimeout(resolve, 100))
})
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <span class="font-semibold leading-6">Generated files</span>
    <div class="flex h-full w-full justify-between gap-1 rounded-md border border-gray-400 p-5">
      <ul v-if="showOutputFiles" class="flex h-full w-full flex-col gap-2">
        <li
          v-for="file in outputFiles"
          :key="file"
          class="flex items-baseline justify-between gap-5 py-1"
        >
          <div class="flex gap-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="currentColor"
            >
              <path
                d="M14 3.3252V7.3252C14 7.59041 14.1054 7.84477 14.2929 8.0323C14.4804 8.21984 14.7348 8.3252 15 8.3252H19M14 3.3252H7C6.46957 3.3252 5.96086 3.53591 5.58579 3.91098C5.21071 4.28605 5 4.79476 5 5.3252V19.3252C5 19.8556 5.21071 20.3643 5.58579 20.7394C5.96086 21.1145 6.46957 21.3252 7 21.3252H17C17.5304 21.3252 18.0391 21.1145 18.4142 20.7394C18.7893 20.3643 19 19.8556 19 19.3252V8.3252M14 3.3252L19 8.3252M8 15.3252H16M11 11.3252V18.3252M8 11.3252H16V18.3252H8V11.3252Z"
                stroke="#0A2540"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <p class="truncate font-semibold leading-6">{{ file }}</p>
          </div>
          <div class="flex w-fit items-center justify-between gap-5">
            <button @click="downloadFile(file)">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="20"
                width="20"
                viewBox="0 0 512 512"
                fill="currentColor"
              >
                <path
                  d="M288 32c0-17.7-14.3-32-32-32s-32 14.3-32 32V274.7l-73.4-73.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0l128-128c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L288 274.7V32zM64 352c-35.3 0-64 28.7-64 64v32c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V416c0-35.3-28.7-64-64-64H346.5l-45.3 45.3c-25 25-65.5 25-90.5 0L165.5 352H64zm368 56a24 24 0 1 1 0 48 24 24 0 1 1 0-48z"
                />
              </svg>
            </button>
          </div>
        </li>
      </ul>
      <span v-else class="font-thin">No files generated</span>
    </div>
  </div>
</template>
